import { taskIdSchema } from "@repo/api/schemas";
import { isManualTask, useTasks } from "@repo/data-store";
import type { TaskId } from "@repo/domain";
import { buttonVariants } from "@repo/ui/components/ui/button";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { TaskDetailContainer } from "@/features/tasks";

export const Route = createFileRoute("/tasks/$taskId")({
  component: TaskDetailComponent,
});

const TASK_STATUS_ORDER = ["in-progress", "draft", "ready", "on-hold", "completed", "cancelled", "failed"];

/** Prev/next within the task list's own ordering — the surface the user
 *  arrived from. The inbox passes its own neighbours instead. */
function useTaskListNeighbours(taskId: TaskId): { prevId: TaskId | null; nextId: TaskId | null } {
  const { tasks: allTasks } = useTasks();
  return useMemo(() => {
    if (!allTasks.length) return { prevId: null, nextId: null };
    const sorted = allTasks.filter(isManualTask).sort((a, b) => {
      const sa = TASK_STATUS_ORDER.indexOf(a.status ?? "");
      const sb = TASK_STATUS_ORDER.indexOf(b.status ?? "");
      if (sa !== sb) return sa - sb;
      const idA = a.identifier?.[0]?.value ?? "";
      const idB = b.identifier?.[0]?.value ?? "";
      return idB.localeCompare(idA, undefined, { numeric: true });
    });
    const idx = sorted.findIndex((t) => t.taskId === taskId);
    if (idx === -1) return { prevId: null, nextId: null };
    return {
      prevId: idx > 0 ? (sorted[idx - 1]?.taskId ?? null) : null,
      nextId: idx < sorted.length - 1 ? (sorted[idx + 1]?.taskId ?? null) : null,
    };
  }, [allTasks, taskId]);
}

function TaskDetailComponent() {
  const { taskId: taskIdParam } = Route.useParams();
  const taskId = taskIdSchema.parse(taskIdParam);
  const navigate = useNavigate();
  const { prevId, nextId } = useTaskListNeighbours(taskId);

  return (
    <TaskDetailContainer
      taskId={taskId}
      prevId={prevId}
      nextId={nextId}
      onNavigate={(id) => navigate({ to: "/tasks/$taskId", params: { taskId: id } })}
      notFound={
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <p className="text-muted-foreground">Task not found</p>
          <Link to="/tasks" className={buttonVariants({ variant: "outline", size: "sm" })}>
            Back to tasks
          </Link>
        </div>
      }
    />
  );
}
